<script lang="ts">
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import {
    loadPersisted, migrateSaved, startAutoPersist, localOwner,
    mapReady, candidates, friends, trips, activeCandidateId, placementMode, enabledAmenityKeys,
  } from './lib/state';
  import { activeAmenities, bestTripMinutes } from './lib/scoring';
  import { computeAllModesForCandidate, findAmenitiesForCandidate, computeTripsForCandidate, fillMissingAmenitiesForAll } from './lib/compute';
  import { initCloudSync } from './lib/sync';
  import { loadCachedSuggestions } from './lib/suggestions';
  import { EXAMPLE_DATA_VERSION, loadExampleData } from './lib/example';
  import type { TravelMode } from './lib/types';
  import Sidebar from './components/Sidebar.svelte';
  import MapView from './components/Map.svelte';
  import MobileCta from './components/MobileCta.svelte';
  import MapWelcome from './components/MapWelcome.svelte';
  import AddSheet from './components/AddSheet.svelte';
  import Toast from './components/Toast.svelte';
  import TravelTimePanel from './components/TravelTimePanel.svelte';
  import NamePromptModal from './components/NamePromptModal.svelte';

  let keyError = '';
  let sidebarOpen = false;
  let desktopCollapsed = false;
  function closeSidebar() {
    sidebarOpen = false;
    if (window.matchMedia('(min-width: 769px)').matches) desktopCollapsed = true;
  }
  function toggleSidebar() {
    if (window.matchMedia('(min-width: 769px)').matches) desktopCollapsed = !desktopCollapsed;
    else sidebarOpen = !sidebarOpen;
  }
  let sidebarRef: Sidebar;
  let hydrated = false;

  const MODES: TravelMode[] = ['DRIVING', 'TRANSIT', 'BICYCLING', 'WALKING'];

  function hydrateLocalState() {
    if (hydrated) return;
    hydrated = true;
    // Only load the local sandbox synchronously, and only if it belongs to the
    // signed-out user. Signed-in (or legacy/untagged) data is left for cloud
    // sync to load once auth resolves — so it never leaks into a logged-out
    // session on reload.
    if (localOwner() === 'anon') {
      const saved = loadPersisted();
      if (saved) migrateSaved(saved);
      if (saved?.exampleLoaded && saved.exampleVersion !== EXAMPLE_DATA_VERSION) {
        loadExampleData();
      }
      if (!get(activeCandidateId) && get(candidates).length) {
        activeCandidateId.set(get(candidates)[0].id);
      }
    }
    // Restore previously-computed ideal neighborhoods (if still valid for the
    // current people/places) so we don't re-spend Places quota on reload.
    loadCachedSuggestions();
  }

  hydrateLocalState();

  function openCandidateFromMap(id: string) {
    sidebarRef.openCandidate(id);
    sidebarOpen = true;
    desktopCollapsed = false;
  }

  // Fill in any computed data missing from restored candidates (mirrors the
  // legacy restoreFromSaved conditional-recompute behaviour).
  function reconcileAfterLoad() {
    const hasFriends = get(friends).length > 0;
    const tr = get(trips);
    for (const c of get(candidates)) {
      const hasResults = MODES.some((m) => Array.isArray(c.results[m]) && c.results[m]!.length);
      if (!hasResults && hasFriends) computeAllModesForCandidate(c.id);

      // Only the enabled picks need to exist; a transient lookup failure
      // ('lookup unavailable') is retried too.
      const wanted = activeAmenities(get(enabledAmenityKeys));
      const hasAllKeys = c.amenities && wanted.every((a) => a.key in c.amenities! && c.amenities![a.key]?.error !== 'lookup unavailable');
      if (!hasAllKeys) findAmenitiesForCandidate(c.id, { onlyMissing: !!c.amenities });

      if (tr.length) {
        const needs = tr.some((t) => {
          const td = c.trips?.[t.id];
          return !td || (bestTripMinutes(td) == null && !('driving' in td) && !('transit' in td));
        });
        if (needs) computeTripsForCandidate(c.id);
      }
    }
  }

  onMount(() => {
    startAutoPersist();
    // When signed in, mirror state to Firestore (states/{uid}); signed out,
    // the localStorage persister above is the only backend.
    initCloudSync();

    const unsub = mapReady.subscribe((ready) => {
      if (!ready) return;
      reconcileAfterLoad();
      unsub();
    });

    // When the user enables new amenity picks, fetch just those for every
    // candidate (disabling is free — scoring simply ignores those keys).
    let lastEnabled = get(enabledAmenityKeys);
    const unsubPrefs = enabledAmenityKeys.subscribe((keys) => {
      const added = [...keys].some((k) => !lastEnabled.has(k));
      lastEnabled = keys;
      if (added && get(mapReady)) fillMissingAmenitiesForAll();
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && get(placementMode) !== 'none') placementMode.set('none');
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      unsubPrefs();
    };
  });
</script>

<button class="menu-toggle" on:click={toggleSidebar} aria-label="Open menu">
  <span aria-hidden="true">☰</span>
</button>
<div class="sidebar-backdrop" class:open={sidebarOpen} on:click={() => (sidebarOpen = false)} aria-hidden="true"></div>

<div class="layout" class:desktop-collapsed={desktopCollapsed}>
  <Sidebar bind:this={sidebarRef} {keyError} open={sidebarOpen} onClose={closeSidebar} />
  <MapView
    on:keyError={(e) => (keyError = e.detail)}
    on:candidateOpen={(e) => openCandidateFromMap(e.detail)}
  >
    <TravelTimePanel />
    <MapWelcome />
  </MapView>
</div>

<MobileCta />
<AddSheet />
<Toast />
<NamePromptModal />
